import './App.css';
import Footer from './components/Footer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';

const NAV_LINKS = [
  { href: '#about', label: 'About Us' },
  { href: '#capabilities', label: 'What We Do' },
  { href: '#industries', label: 'Our Customers' },
  { href: '#quality', label: 'Quality' },
  { href: '#why-us', label: 'Why Us' },
  { href: '#contact', label: 'Contact Us' },
];

const STATS = [
  { value: '20+', label: 'Years of Expertise' },
  { value: 'IATF 16949', label: 'Quality Certified' },
  { value: '450–1000T', label: 'HPDC Clamping Range' },
  { value: 'Multi-Plant', label: 'Manufacturing Footprint' },
];

const CAPABILITIES = [
  {
    icon: 'bi-tools',
    title: 'In-House Toolroom & NPD',
    desc: 'Rapid tool design, maintenance and modification capabilities along with new product development setup.',
  },
  {
    icon: 'bi-cpu',
    title: 'HPDC Lines',
    desc: 'State-of-the-art high pressure die casting cells for consistent quality production.',
  },
  {
    icon: 'bi-gear-wide-connected',
    title: 'CNC Machining',
    desc: 'Precision machining centres for tight-tolerance features and finishing.',
  },
  {
    icon: 'bi-box-seam',
    title: 'Finishing & Assembly',
    desc: 'Surface treatment, shot blasting and sub-assembly services.',
  },
];

const MACHINES = [
  { tonnage: '450T', label: 'Small, Medium' },
  { tonnage: '600T', label: 'Medium', active: true },
  { tonnage: '700T', label: 'Large' },
  { tonnage: '1000T', label: 'Heavy', active: true },
];

const ALLOYS = [
  { name: 'ADC12 & A380 Aluminum', desc: 'Most common for automotive & industrial applications' },
  { name: 'Zinc Zamac Alloys', desc: 'Excellent for thin walls & complex shapes' },
  { name: 'Magnesium Alloys', desc: 'Lightweight with excellent strength-to-weight ratio' },
];

const SPECS = [
  { value: '150g–6kg', label: 'Part Weight Range' },
  { value: '±0.05mm', label: 'Dimensional Accuracy' },
  { value: '1.5mm', label: 'Min. Wall Thickness' },
  { value: 'Ra 3.2–7', label: 'Surface Finish' },
];

const VALUE_ADDED = [
  'Prototype development & sampling',
  'Casting, Shot Blasting and Machining',
  'Powder coating and assembly',
  'Packaging & logistics support',
  'DFM consultation for optimal castability',
  'Mold flow simulation support',
];

const INDUSTRIES = [
  {
    icon: 'bi-car-front',
    title: 'Automotive',
    desc: 'Precision components for engines, transmissions, and chassis systems meeting stringent automotive standards.',
    tags: ['Engine Parts', 'Housings', 'Crankcases', 'ECU Covers'],
  },
  {
    icon: 'bi-lightning-charge',
    title: 'Electrical & Electronics',
    desc: 'EMI/RFI shielding enclosures, heat sinks, and connectors for electronic devices and electrical systems.',
    tags: ['Enclosures', 'Heat Sinks', 'Connectors'],
  },
  {
    icon: 'bi-broadcast',
    title: 'Lighting & 5G',
    desc: 'Heat-dissipating LED housings and fixtures with excellent thermal management. Precision enclosures for 5G telecom infrastructure.',
    tags: ['LED Housings', 'Heat Sinks', 'Enclosures'],
  },
];

const CLIENTS = [
  { name: 'Bajaj Auto', logo: '/logos/bajaj.png' },
  { name: 'Ather', logo: '/logos/ather.jpg', invert: true },
  { name: 'KTM', logo: '/logos/ktm.jpg' },
  { name: 'Endurance', logo: '/logos/endurance.png' },
  { name: 'Hero MotoCorp', logo: '/logos/hero.png' },
];

const QUALITY_TOOLS = [
  { code: 'APQP', name: 'Advanced Product Quality Planning' },
  { code: 'PPAP', name: 'Production Part Approval Process' },
  { code: 'PFMEA', name: 'Process Failure Mode Effects Analysis' },
  { code: 'SPC', name: 'Statistical Process Control' },
  { code: 'MSA', name: 'Measurement System Analysis' },
];

const VALIDATION = [
  'Metallurgical Analysis – Chemical Composition Evaluation (Spectrometer)',
  'Mechanical Testing – UTM for Tensile and Hardness Test',
  'X-Ray Porosity Detection (Third Party)',
  'Visual Standards and Go/No-Go Gauges',
  'Full Traceability from Heat-wise Raw Material to Final Finished Component',
];

const TIMELINE = [
  { year: '2001', icon: 'bi-rocket-takeoff', event: 'Commenced high pressure die casting operations at our Waluj facility.' },
  { year: '2002', icon: 'bi-stars', event: 'Added in-house fettling and shot blasting finishing capabilities.' },
  { year: '2008', icon: 'bi-thermometer-half', event: 'Introduced stress-relieving processes for auto components.' },
  { year: '2010', icon: 'bi-graph-up-arrow', event: 'Expanded die casting capacity to support growing customer demand.' },
  { year: '2015', icon: 'bi-diagram-3', event: 'Diversified into plastic injection moulding.' },
  { year: '2016', icon: 'bi-arrows-angle-expand', event: 'Further scaled die casting capacity with additional production lines.' },
  { year: '2020', icon: 'bi-fire', event: 'Enhanced melting capacity and added new machines for higher output.' },
  { year: '2026–27', icon: 'bi-robot', event: 'Expansion at BIDKIN DMIC with a fully automated casting and machining setup.', future: true },
];

const WHY_CHOOSE = [
  { icon: 'bi-award', title: '20+ Years Experience', desc: 'Two decades of proven expertise in die casting with deep industry knowledge.' },
  { icon: 'bi-building', title: 'Modern Infrastructure', desc: 'State-of-the-art machinery from 450T to 1000T and integrated manufacturing ecosystem.' },
  { icon: 'bi-patch-check', title: 'Quality Systems', desc: 'ISO 9001 & IATF 16949 certified with comprehensive quality control and full traceability.' },
  { icon: 'bi-people', title: 'Skilled Workforce', desc: 'Experienced team with deep expertise and a continuous-improvement culture.' },
  { icon: 'bi-clock-history', title: 'Reliable On-Time Delivery', desc: 'Disciplined production scheduling with a strong delivery track record.' },
  { icon: 'bi-currency-rupee', title: 'Competitive Pricing', desc: 'Cost-effective solutions without compromising on quality standards.' },
];

const GREEN_STATS = [
  { val: '200kW', lbl: 'Solar Capacity', icon: 'bi-sun-fill' },
  { val: '180T', lbl: 'CO₂ Offset / Year', icon: 'bi-cloud-check-fill' },
  { val: '7,000', lbl: 'Tree Equivalent / Year', icon: 'bi-tree-fill' },
  { val: 'CSR', lbl: 'Corporate Commitment', icon: 'bi-heart-fill' },
];

function HomePage() {
  return (
    <>

      {/* ── Header ── */}
      <header className="site-header">
        <div className="container">
          <nav className="navbar navbar-expand-lg px-0">
            <a className="navbar-brand" href="#home">
              <img src="/logo.png" alt="Bhavani Die Casting Private Limited" className="nav-logo" />
            </a>
            <button
              className="navbar-toggler border-0"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#mainNav"
              aria-controls="mainNav"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon" />
            </button>
            <div className="collapse navbar-collapse" id="mainNav">
              <ul className="navbar-nav ms-auto align-items-lg-center gap-lg-1 mb-2 mb-lg-0">
                {NAV_LINKS.map((l) => (
                  <li key={l.href} className="nav-item">
                    <a className="nav-link" href={l.href}>{l.label}</a>
                  </li>
                ))}
                <li className="nav-item ms-lg-3">
                  <a className="btn btn-brand rounded-pill px-4" href="mailto:contact@bhvanidc.com">
                    Request a Quote
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </header>

      <main>

        {/* ── Hero ── */}
        <section id="home" className="hero-section">
          <div className="container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-7">
                <p className="hero-eyebrow">Trusted Partners to OEMs &amp; Tier-1 Suppliers Since 2001</p>
                <h1 className="hero-title">Precision Engineering<br />Excellence</h1>
                <p className="hero-sub">
                  High Pressure Die Casting solutions for automotive, electrical,
                  and industrial applications — concept to production.
                </p>
                <div className="hero-pillars">
                  <div className="hero-pillar">
                    <span className="pillar-label">PROCESS</span>
                    <span className="pillar-val">High Pressure Die Casting</span>
                  </div>
                  <div className="hero-pillar">
                    <span className="pillar-label">QUALITY</span>
                    <span className="pillar-val">ISO 9001 · IATF 16949</span>
                  </div>
                  <div className="hero-pillar">
                    <span className="pillar-label">SERVICE</span>
                    <span className="pillar-val">Concept to Production</span>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-3 mt-4">
                  <a className="btn btn-white rounded-pill px-4 py-2" href="#contact">Request a Quote</a>
                  <a className="btn btn-outline-white rounded-pill px-4 py-2" href="#capabilities">Our Capabilities</a>
                </div>
              </div>
              <div className="col-lg-5 d-none d-lg-block">
                <div className="hero-badge-grid">
                  {[
                    { icon: 'bi-shield-check', text: 'ISO 9001 Certified' },
                    { icon: 'bi-award', text: 'IATF 16949 Aligned' },
                    { icon: 'bi-calendar-check', text: 'Est. 2001' },
                    { icon: 'bi-geo-alt', text: 'Chh. Sambhajinagar (Aurangabad)' },
                  ].map((b) => (
                    <div key={b.text} className="hero-badge-item">
                      <i className={`bi ${b.icon} hero-badge-icon`} />
                      <span>{b.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Stats Strip ── */}
        <div className="stats-strip">
          <div className="container">
            <div className="row g-0 text-center">
              {STATS.map((s) => (
                <div key={s.label} className="col-6 col-lg-3">
                  <div className="stat-item">
                    <div className="stat-val">{s.value}</div>
                    <div className="stat-lbl">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── About ── */}
        <section id="about" className="section">
          <div className="container">
            <div className="row g-5 align-items-start">
              <div className="col-lg-5">
                <span className="eyebrow">Who We Are</span>
                <h2>2.5 Decades of Legacy in Precision Die Casting</h2>
                <p>
                  Since 2001, Bhavani Die Casting Private Limited has built deep expertise in
                  intricate casting technologies and processes to meet the stringent demands of
                  global Tier-1 suppliers and OEMs.
                </p>
                <p>
                  Our mission is to be the world's most trusted die casting company — combining
                  advanced manufacturing, uncompromising ethics, and continuous innovation to create
                  products that power industries and build a legacy of integrity for the next
                  generation.
                </p>
                <div className="mt-4">
                  <span className="eyebrow">Automotive Focus Areas</span>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {[
                      { icon: 'bi-car-front', label: 'Automobile Structural Parts' },
                      { icon: 'bi-lightning', label: 'E-Mobility' },
                      { icon: 'bi-thermometer-half', label: 'Thermal Systems' },
                    ].map((f) => (
                      <span key={f.label} className="focus-tag">
                        <i className={`bi ${f.icon} me-1`} />{f.label}
                      </span>
                    ))}
                    <span className="focus-tag">
                      <i className="bi bi-plus-circle me-1" />Other Casting Solutions
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-7">
                <span className="eyebrow">Integrated Capabilities</span>
                <div className="row g-3 mt-1">
                  {CAPABILITIES.map((c) => (
                    <div key={c.title} className="col-sm-6">
                      <div className="cap-card">
                        <i className={`bi ${c.icon} cap-icon`} />
                        <h3 className="h6 mt-3 mb-1">{c.title}</h3>
                        <p className="small text-secondary mb-0">{c.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Equipment & Capacity ── */}
        <section id="equipment" className="section section-soft">
          <div className="container">
            <span className="eyebrow">Equipment &amp; Capacity</span>
            <h2 className="mb-5">Advanced Die Casting Machinery</h2>
            <div className="row g-5 align-items-start">
              <div className="col-lg-6">
                <h3 className="h5 mb-3">Cold Chamber HPDC Machines</h3>
                <p className="text-secondary mb-4">
                  Equipped with modern cold chamber High Pressure Die Casting machines ranging from
                  450T to 1000T clamping force, enabling us to produce parts from 150g to 6kg.
                </p>
                <div className="machine-row">
                  {MACHINES.map((m) => (
                    <div key={m.tonnage} className={`machine-tile${m.active ? ' machine-tile--active' : ''}`}>
                      <div className="machine-ton">{m.tonnage}</div>
                      <div className="machine-lbl">{m.label}</div>
                    </div>
                  ))}
                </div>
                <div className="row g-3 mt-4">
                  <div className="col-6">
                    <div className="equip-box">
                      <i className="bi bi-fire equip-icon" />
                      <h4 className="h6 mt-2 mb-1">Melting &amp; Holding</h4>
                      <p className="small text-secondary mb-0">
                        Electric furnaces with precise temperature control and automatic dosing systems.
                      </p>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="equip-box">
                      <i className="bi bi-robot equip-icon" />
                      <h4 className="h6 mt-2 mb-1">Automation</h4>
                      <p className="small text-secondary mb-0">
                        Automated ladling, spraying, and extraction for improved efficiency.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="tech-note mt-4">
                  <p className="small mb-2 fw-semibold">Machine Technology Highlights</p>
                  <ul className="list-unstyled mb-0">
                    {[
                      'Energy-efficient servo-driven machine systems',
                      'Advanced PLC control with real-time process display',
                      'High-durability platens engineered for long production life',
                      'Automated die-change support for reduced downtime',
                      'Integrated online quality monitoring',
                    ].map((t) => (
                      <li key={t} className="tech-point">
                        <i className="bi bi-circle-fill me-2" />{t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="capacity-panel">
                  <h3 className="h5 mb-4">Production Strengths</h3>
                  {[
                    { label: 'Volume Production', val: 'Scalable', pct: 86 },
                    { label: 'Capacity Utilization', val: 'Optimized', pct: 80 },
                    { label: 'Machine Availability', val: 'High', pct: 94 },
                  ].map((r) => (
                    <div key={r.label} className="cap-row">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="small">{r.label}</span>
                        <strong className="small">{r.val}</strong>
                      </div>
                      <div className="cap-bar">
                        <div className="cap-fill" style={{ width: `${r.pct}%` }} />
                      </div>
                    </div>
                  ))}
                  <div className="key-caps mt-4">
                    <p className="small fw-semibold mb-3">Key Capabilities</p>
                    {[
                      'Thin walls: 1.5mm minimum',
                      'Tight tolerances: ±0.05mm',
                      'Complex geometries',
                      'Excellent surface finish',
                    ].map((c) => (
                      <div key={c} className="d-flex align-items-center gap-2 mb-2">
                        <i className="bi bi-check2 check-blue" />
                        <span className="small">{c}</span>
                      </div>
                    ))}
                  </div>
                  <div className="automation-note mt-4">
                    <p className="small fw-semibold mb-2">Automation &amp; Smart Systems</p>
                    <div className="d-flex flex-wrap gap-2">
                      {['Spectrometer', 'Density Index', 'N-2 Degassing', 'UTM Testing', 'Auto Extractor', 'Auto Sprayer', 'Auto Ladler', 'Online QC'].map((a) => (
                        <span key={a} className="auto-tag">{a}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Full-Service Capabilities ── */}
        <section id="capabilities" className="section">
          <div className="container">
            <span className="eyebrow">Full-Service Capabilities</span>
            <h2 className="mb-5">Comprehensive Production Capabilities</h2>
            <div className="row g-4">
              <div className="col-lg-4">
                <h3 className="h5 mb-4">Alloy Portfolio</h3>
                {ALLOYS.map((a) => (
                  <div key={a.name} className="alloy-item">
                    <span className="alloy-dot" />
                    <div>
                      <strong className="small d-block">{a.name}</strong>
                      <span className="text-secondary" style={{ fontSize: '0.8rem' }}>{a.desc}</span>
                    </div>
                  </div>
                ))}
                <div className="mt-4">
                  <h3 className="h5 mb-3">Technical Specifications</h3>
                  <div className="row g-2">
                    {SPECS.map((s) => (
                      <div key={s.label} className="col-6">
                        <div className="spec-card">
                          <div className="spec-val">{s.value}</div>
                          <div className="spec-lbl">{s.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <h3 className="h5 mb-4">Value-Added Services</h3>
                {VALUE_ADDED.map((v) => (
                  <div key={v} className="val-item">
                    <i className="bi bi-check-circle-fill check-blue me-2" />
                    <span className="small">{v}</span>
                  </div>
                ))}
                <div className="mt-4">
                  <h3 className="h5 mb-3">Core Competencies</h3>
                  {[
                    { icon: 'bi-fire', label: 'HPDC Technology', desc: 'Cold chamber machines delivering complex geometries with exceptional precision.' },
                    { icon: 'bi-layers', label: 'Alloy Mastery', desc: 'ADC12, A380, ALSI-132, Zinc Zamac, and Magnesium alloys.' },
                    { icon: 'bi-pencil-square', label: 'Mould Design', desc: 'In-house design with flow simulation for optimal casting results.' },
                    { icon: 'bi-gear', label: 'Secondary Operations', desc: 'Complete CNC machining, drilling, tapping, and surface finishing under one roof.' },
                  ].map((c) => (
                    <div key={c.label} className="competency-item">
                      <i className={`bi ${c.icon} comp-icon`} />
                      <div>
                        <strong className="small d-block">{c.label}</strong>
                        <span className="text-secondary" style={{ fontSize: '0.78rem' }}>{c.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-lg-4">
                <h3 className="h5 mb-4">Skilled Workforce</h3>
                <div className="workforce-panel">
                  {[
                    { val: 'Skilled', lbl: 'Trained Operators' },
                    { val: 'In-House', lbl: 'Engineering Team' },
                    { val: '15+ yrs', lbl: 'Avg. Experience' },
                  ].map((w) => (
                    <div key={w.lbl} className="workforce-stat">
                      <div className="workforce-val">{w.val}</div>
                      <div className="workforce-lbl">{w.lbl}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <h3 className="h5 mb-3">Continuous Improvement</h3>
                  {[
                    'Regular Training Programs — Skill enhancement and safety protocols',
                    'Process Optimization — Lean manufacturing principles adoption',
                    'Technology Upgrades — Investment in latest equipment',
                    'Quality Focus — Zero-defect manufacturing culture',
                  ].map((p) => (
                    <div key={p} className="improve-item">
                      <i className="bi bi-check-circle-fill check-blue me-2 flex-shrink-0" />
                      <span className="small">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Industries ── */}
        <section id="industries" className="section section-soft">
          <div className="container">
            <span className="eyebrow">Diverse Applications</span>
            <h2 className="mb-5">Industry Applications &amp; Solutions</h2>
            <div className="row g-4">
              {INDUSTRIES.map((ind) => (
                <div key={ind.title} className="col-md-4">
                  <div className="industry-card">
                    <i className={`bi ${ind.icon} industry-icon`} />
                    <h3 className="h5 mt-3 mb-2">{ind.title}</h3>
                    <p className="small text-secondary mb-3">{ind.desc}</p>
                    <div className="d-flex flex-wrap gap-2">
                      {ind.tags.map((t) => (
                        <span key={t} className="ind-tag">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="clients-band">
              <p className="clients-title">Our components power vehicles from leading brands</p>
              <div className="clients-row">
                {CLIENTS.map((c) => (
                  <span
                    key={c.name}
                    className={`client-logo${c.invert ? ' client-logo--invert' : ''}`}
                    title={c.name}
                  >
                    {c.logo ? <img src={c.logo} alt={c.name} /> : c.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Quality ── */}
        <section id="quality" className="section">
          <div className="container">
            <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
              <span className="eyebrow mb-0">Quality &amp; Inspection</span>
              <span className="cert-badge">ISO IATF 16949:2016</span>
            </div>
            <h2 className="mb-1">Automotive-Grade Quality Management</h2>
            <p className="text-secondary mb-5">
              Comprehensive quality systems with full traceability and in-process controls across
              every stage of manufacturing.
            </p>
            <div className="row g-5">
              <div className="col-lg-6">
                <h3 className="h5 mb-4">Quality Tools</h3>
                <div className="row g-3">
                  {QUALITY_TOOLS.map((q) => (
                    <div key={q.code} className="col-sm-6">
                      <div className="quality-card">
                        <div className="quality-code">{q.code}</div>
                        <div className="quality-name">{q.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <h4 className="h6 mb-2">In-Process Controls &amp; Traceability</h4>
                  <p className="text-secondary small mb-3">
                    Fully integrated die casting setup with injection-curve control, online QC, and
                    fabrication management systems for end-to-end process discipline.
                  </p>
                  <ul className="list-unstyled mb-0">
                    {[
                      'Shot Monitoring — Real-time monitoring for casting consistency',
                      'Cavity Pressure — Sensor-based control for casting integrity',
                      'Change Management — Strictly controlled drawing and tooling revisions',
                    ].map((p) => (
                      <li key={p} className="quality-point">
                        <i className="bi bi-dot" />{p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="col-lg-6">
                <h3 className="h5 mb-4">Validation Capabilities</h3>
                <div className="validation-list">
                  {VALIDATION.map((v) => (
                    <div key={v} className="validation-item">
                      <i className="bi bi-check-circle-fill flex-shrink-0" />
                      <span className="small">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Timeline ── */}
        <section id="timeline" className="section section-soft">
          <div className="container">
            <span className="eyebrow">Business Evolution &amp; Legacy</span>
            <h2>Building Trust Through Precision Engineering</h2>
            <p className="text-secondary mb-5">
              From a focused die casting startup to a fully automated multi-plant operation
              spanning over two decades.
            </p>
            <div className="timeline">
              <div className="timeline-line" aria-hidden="true" />
              {TIMELINE.map((t, i) => (
                <div
                  key={t.year}
                  className={`tl-item${i % 2 ? ' tl-item--right' : ''}${t.future ? ' tl-item--future' : ''}`}
                >
                  <div className="tl-marker">
                    <i className={`bi ${t.icon}`} />
                  </div>
                  <div className="tl-card">
                    <span className="tl-year">{t.year}</span>
                    <p className="tl-event">{t.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Why Choose ── */}
        <section id="why-us" className="section why-section">
          <div className="container">
            <span className="eyebrow why-eyebrow">Value Proposition</span>
            <h2 className="why-h2 mb-5">Why Choose Bhavani Die Casting</h2>
            <div className="row g-4">
              {WHY_CHOOSE.map((w) => (
                <div key={w.title} className="col-md-6 col-xl-4">
                  <div className="why-card">
                    <i className={`bi ${w.icon} why-icon`} />
                    <h3 className="h6 mt-3 mb-1">{w.title}</h3>
                    <p className="small mb-0">{w.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Green Manufacturing ── */}
        <section className="section green-section">
          <div className="container">
            <div className="row g-5 align-items-center">
              <div className="col-lg-6">
                <span className="eyebrow green-eyebrow">Sustainability</span>
                <h2 className="green-h2">Green Manufacturing</h2>
                <p className="green-text">
                  Integrating green technology into smart manufacturing — supporting both innovation
                  and environmental responsibility.
                </p>
                <p className="green-text">
                  <strong>200kW Solar System Installed</strong> — Environmental impact protection
                  committed to Mother Earth.
                </p>
                <ul className="list-unstyled mt-3">
                  {[
                    'Significant reduction in carbon emissions',
                    '~180 tonnes of CO₂ offset annually',
                    'Equivalent to planting 7,000 trees annually',
                    'Supports corporate sustainability goals',
                    'Commitment to eco-consciousness as corporate social responsibility (CSR)',
                    'Encouragement to community to adopt green solutions',
                  ].map((p) => (
                    <li key={p} className="green-point">
                      <i className="bi bi-leaf-fill me-2" />{p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="row g-3">
                  {GREEN_STATS.map((g) => (
                    <div key={g.lbl} className="col-6">
                      <div className="green-card">
                        <i className={`bi ${g.icon} green-icon`} />
                        <div className="green-num">{g.val}</div>
                        <div className="green-lbl">{g.lbl}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Contact ── */}
        <section id="contact" className="section">
          <div className="container">
            <span className="eyebrow">Contact</span>
            <h2 className="mb-2">Let's Discuss Your Next Die Casting Requirement</h2>
            <p className="text-secondary mb-5">
              Reach out for quotes, technical consultation, and partnership inquiries.
            </p>
            <div className="row g-4">
              <div className="col-md-4">
                <div className="contact-card">
                  <div className="contact-icon-wrap">
                    <i className="bi bi-geo-alt-fill" />
                  </div>
                  <h3 className="h6 mt-3 mb-2">Address</h3>
                  <p className="small text-secondary mb-0">
                    Plot No. 36, Sector-16, DMIC, Bidkin,<br />
                    District Chhatrapati Sambhajinagar<br />
                    (Aurangabad), Maharashtra - 431105
                  </p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="contact-card">
                  <div className="contact-icon-wrap">
                    <i className="bi bi-telephone-fill" />
                  </div>
                  <h3 className="h6 mt-3 mb-2">Phone</h3>
                  <p className="small text-secondary mb-1">+91 9545130777</p>
                  <p className="small text-secondary mb-3">+91 9595909599</p>
                  <a className="link-brand small" href="tel:+919545130777">Call Now →</a>
                </div>
              </div>
              <div className="col-md-4">
                <div className="contact-card">
                  <div className="contact-icon-wrap">
                    <i className="bi bi-envelope-fill" />
                  </div>
                  <h3 className="h6 mt-3 mb-2">Email</h3>
                  <p className="small text-secondary mb-3">contact@bhvanidc.com</p>
                  <a className="link-brand small" href="mailto:contact@bhvanidc.com">Send Email →</a>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
    </>
  );
}

function App() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';

  let page;
  if (path === '/privacy-policy') page = <PrivacyPolicy />;
  else if (path === '/terms-and-conditions') page = <TermsAndConditions />;
  else page = <HomePage />;

  return (
    <div className="site-shell">
      {page}
      <Footer />
    </div>
  );
}

export default App;
